// FigJam-style widgets: code using the widget API (AutoLayout, Text, useSyncedState,
// usePropertyMenu, widget.register…) runs in a sandbox, renders into ordinary layers inside the
// widget's frame, and keeps its synced state on the widget node (so it syncs through multiplayer).

import { WIDGET_RUNTIME, applyWidgetRender, transformJsx, type NodeId, type WidgetElement, type WidgetRender } from '@vellum/core';
import { app } from './app.ts';

/** Run the widget's code once (render, click or menu action) in a throwaway sandbox. */
function runSandbox(code: string, state: Record<string, unknown>, action: unknown): Promise<WidgetRender> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-scripts'); // opaque origin: no access to the app
    frame.style.display = 'none';
    const id = Math.random().toString(36).slice(2);
    let js = code;
    try {
      // JSX (as in Figma's docs) compiles to figma.widget.h(...)
      if (/<[A-Za-z>]/.test(code)) js = transformJsx(code);
    } catch (e) {
      return reject(e as Error);
    }
    const safe = js.replace(/<\/script/gi, '<\\/script');
    const host = `const __notify = (t) => parent.postMessage({ __widget: 'notify', text: t }, '*');
addEventListener('message', async (e) => {
  const m = e.data || {}; if (m.__widget !== 'run') return;
  try { const r = await __run(m.state, m.action); parent.postMessage({ __widget: 'done', id: m.id, ...r }, '*'); }
  catch (err) { parent.postMessage({ __widget: 'done', id: m.id, error: String((err && err.message) || err) }, '*'); }
});`;
    frame.srcdoc = `<!doctype html><script>${WIDGET_RUNTIME}\n${host}\ntry {\n${safe}\n} catch (e) { parent.postMessage({ __widget: 'done', id: 'boot', error: String(e && e.message || e) }, '*'); }<\/script>`;
    const timer = setTimeout(() => finish(new Error('The widget took too long')), 3000);
    const onMsg = (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return;
      const m = e.data as { __widget?: string; id?: string; tree?: WidgetElement; state?: Record<string, unknown>; menu?: { propertyName: string; tooltip: string }[] | null; error?: string; text?: string };
      if (m?.__widget === 'notify') return app.toast(String(m.text).slice(0, 200));
      if (m?.__widget !== 'done') return;
      if (m.error) finish(new Error(m.error));
      else finish(null, { tree: m.tree!, state: m.state ?? {}, menu: m.menu ?? null });
    };
    const finish = (err: Error | null, v?: WidgetRender) => {
      clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      frame.remove();
      if (err) reject(err);
      else resolve(v!);
    };
    window.addEventListener('message', onMsg);
    frame.onload = () => frame.contentWindow?.postMessage({ __widget: 'run', id, state, action }, '*');
    document.body.appendChild(frame);
  });
}

/** Re-render a widget into its frame (one undoable step). */
async function rerender(widgetId: NodeId, action: unknown) {
  const ed = app.editor;
  const n = app.doc.get(widgetId);
  if (!n?.widget) return;
  if (app.readOnly) return app.toast('This file is view-only');
  let out;
  try {
    out = await runSandbox(n.widget.code, structuredClone(n.widget.state ?? {}), action);
  } catch (e) {
    return app.toast(`Widget error: ${(e as Error).message}`);
  }
  const fresh = app.doc.get(widgetId);
  if (!fresh?.widget) return;
  applyWidgetRender(ed, widgetId, out);
}

export function insertWidget(name: string, code: string) {
  const ed = app.editor;
  const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  // don't stack on top of another widget placed at the same spot
  let x = Math.round(c.x - 80),
    y = Math.round(c.y - 40);
  while (app.doc.children(ed.pageId).some((n) => n.widget && Math.abs(n.x - x) < 8 && Math.abs(n.y - y) < 8)) (x += 32), (y += 32);
  const id = ed.create('FRAME', { name, x, y, width: 160, height: 80, widget: { name, code, state: {} } } as never);
  ed.setSelection([id]);
  void rerender(id, null);
  return id;
}

/** The widget a layer belongs to (itself or an ancestor). */
export function widgetOf(id: NodeId): NodeId | null {
  for (const n of [app.doc.get(id), ...app.doc.ancestors(id)]) if (n?.widget) return n.id;
  return null;
}

/** A click on a widget's layer: run its onClick if it has one. Returns true when handled. */
export function widgetClick(id: NodeId): boolean {
  const w = widgetOf(id);
  if (!w) return false;
  const n = app.doc.get(id)!;
  const inside = [n, ...app.doc.ancestors(id)].filter((x) => x.id === w || widgetOf(x.id) === w);
  const field = inside.find((x) => x.pluginData?.widgetInput);
  if (field) {
    editInput(w, field.id);
    return true;
  }
  const target = inside.find((x) => x.pluginData?.widgetClick);
  if (!target) return false;
  void rerender(w, { kind: 'click', path: target.pluginData!.widgetPath });
  return true;
}

/** Edit a widget Input in place: an HTML field over the rendered one; Enter or blur commits. */
function editInput(widgetId: NodeId, fieldId: NodeId) {
  const b = app.doc.worldBounds(fieldId);
  const tl = app.pageToScreen({ x: b.x, y: b.y });
  const br = app.pageToScreen({ x: b.x + b.width, y: b.y + b.height });
  const host = document.querySelector('.workspace canvas')?.parentElement ?? document.body;
  const r = host.getBoundingClientRect();
  const input = document.createElement('input');
  input.className = 'widget-input';
  input.value = app.doc.get(fieldId)?.pluginData?.widgetValue ?? '';
  Object.assign(input.style, { left: `${r.left + tl.x}px`, top: `${r.top + tl.y}px`, width: `${Math.max(80, br.x - tl.x)}px`, height: `${Math.max(24, br.y - tl.y)}px` });
  let done = false;
  const commit = () => {
    if (done) return;
    done = true;
    input.remove();
    void rerender(widgetId, { kind: 'input', path: app.doc.get(fieldId)?.pluginData?.widgetPath, characters: input.value });
  };
  input.onkeydown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') {
      done = true;
      input.remove();
    }
  };
  input.onblur = commit;
  document.body.appendChild(input);
  input.focus();
  input.select();
}

export function widgetMenu(id: NodeId, propertyName: string) {
  void rerender(id, { kind: 'menu', propertyName });
}

export const SAMPLE_WIDGETS: { name: string; code: string }[] = [
  {
    name: 'Team note (JSX)',
    code: `const { widget } = figma;
const { AutoLayout, Text, Input, useSyncedState } = widget;

function TeamNote() {
  const [text, setText] = useSyncedState('text', '');
  const [edits, setEdits] = useSyncedState('edits', 0);
  return (
    <AutoLayout direction="vertical" spacing={8} padding={16} fill="#e5f4ff" cornerRadius={12} width={260}>
      <Text fontSize={14} fontWeight={700}>Team note</Text>
      <Input
        value={text}
        placeholder="Type a note…"
        onTextEditEnd={(e) => { setText(e.characters); setEdits(edits + 1); }}
        width="fill-parent"
      />
      <Text fontSize={11} fill="#6b6b6b">{edits === 0 ? 'Nobody has edited this yet' : 'Edited ' + edits + ' time' + (edits === 1 ? '' : 's')}</Text>
    </AutoLayout>
  );
}

widget.register(TeamNote);`,
  },
  {
    name: 'Counter',
    code: `const { widget } = figma;
const { AutoLayout, Text, useSyncedState, usePropertyMenu, h } = widget;
function Counter() {
  const [count, setCount] = useSyncedState('count', 0);
  usePropertyMenu([{ itemType: 'action', propertyName: 'reset', tooltip: 'Reset' }], () => setCount(0));
  return h(AutoLayout, { direction: 'horizontal', spacing: 12, padding: { vertical: 12, horizontal: 16 }, fill: '#ffffff', cornerRadius: 12, stroke: '#e6e6e6', verticalAlignItems: 'center' },
    h(AutoLayout, { name: 'minus', onClick: () => setCount(count - 1), fill: '#f0f0f0', cornerRadius: 8, padding: { vertical: 4, horizontal: 12 } }, h(Text, { fontSize: 20 }, '−')),
    h(Text, { fontSize: 28, fontWeight: 700 }, String(count)),
    h(AutoLayout, { name: 'plus', onClick: () => setCount(count + 1), fill: '#0d99ff', cornerRadius: 8, padding: { vertical: 4, horizontal: 12 } }, h(Text, { fontSize: 20, fill: '#ffffff' }, '+')),
  );
}
widget.register(Counter);`,
  },
  {
    name: 'Checklist',
    code: `const { widget } = figma;
const { AutoLayout, Text, useSyncedState, h } = widget;
const ITEMS = ['Write the brief', 'Sketch ideas', 'Review with the team', 'Ship it'];
function Checklist() {
  const [done, setDone] = useSyncedState('done', []);
  const toggle = (i) => setDone(done.includes(i) ? done.filter((x) => x !== i) : [...done, i]);
  return h(AutoLayout, { direction: 'vertical', spacing: 8, padding: 16, fill: '#fff8e1', cornerRadius: 12, width: 260 },
    h(Text, { fontSize: 16, fontWeight: 700 }, 'Checklist · ' + done.length + '/' + ITEMS.length),
    ...ITEMS.map((t, i) => h(AutoLayout, { name: 'item', onClick: () => toggle(i), spacing: 8, verticalAlignItems: 'center', width: 'fill-parent' },
      h(Text, { fontSize: 16 }, done.includes(i) ? '☑' : '☐'),
      h(Text, { fontSize: 14, fill: done.includes(i) ? '#888888' : '#1e1e1e' }, t))),
  );
}
widget.register(Checklist);`,
  },
];
